import { useMemo, useState } from 'react';
import { showToast } from '@repo/ui';

import { PageLayout } from '@xtnd/web/shared/layout';

import type { PublicCredentialMetadata } from '../api/clioCredentialsApi';
import {
  AddCredentialDialog,
  ConfirmRemoveDialog,
  CredentialList,
  EditLabelDialog,
  useClioOAuthFlow,
} from '../components/clio-integration';
import {
  useClioCredentials,
  useInvalidateClioCredentials,
  useReauthorizeClioCredential,
  useUpdateClioCredential,
} from '../queries/useClioCredentialsQuery';

export default function ClioIntegrationPage() {
  const { data, isLoading, isError, error, refetch } = useClioCredentials();
  const invalidate = useInvalidateClioCredentials();
  const reauthorizeMutation = useReauthorizeClioCredential();
  const updateMutation = useUpdateClioCredential();

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<PublicCredentialMetadata | null>(null);
  const [removeTarget, setRemoveTarget] = useState<PublicCredentialMetadata | null>(null);
  const [busyCredentialId, setBusyCredentialId] = useState<string | null>(null);

  const reauthFlow = useClioOAuthFlow({
    onSuccess: () => {
      invalidate();
    },
    onError: (msg) => {
      showToast.error(msg);
    },
  });

  const sortedCredentials = useMemo(() => {
    const list = data?.credentials ?? [];
    return [...list].sort((a, b) => {
      if (a.role === b.role) return a.label.localeCompare(b.label);
      return a.role === 'primary' ? -1 : 1;
    });
  }, [data?.credentials]);

  const isFirstCredential = sortedCredentials.length === 0;

  const handleReauthorize = async (c: PublicCredentialMetadata) => {
    setBusyCredentialId(c.credentialId);
    try {
      const result = await reauthorizeMutation.mutateAsync({
        credentialId: c.credentialId,
        request: { redirectUri: `${window.location.origin}/clio-redirect` },
      });
      await reauthFlow.startOAuth(result.oauthAuthorizeUrl, result.state);
    } catch {
      // Toast surfaced via hook/mutation onError
    } finally {
      setBusyCredentialId(null);
    }
  };

  const handleToggleEnabled = async (c: PublicCredentialMetadata) => {
    setBusyCredentialId(c.credentialId);
    try {
      await updateMutation.mutateAsync({
        credentialId: c.credentialId,
        patch: { enabled: !c.enabled },
      });
      showToast.success(c.enabled ? 'Credential disabled' : 'Credential enabled');
    } catch {
      // Toast surfaced via mutation onError
    } finally {
      setBusyCredentialId(null);
    }
  };

  return (
    <PageLayout
      title="Clio Integration"
      description="Manage the Clio apps connected to your workspace."
      icon="ClioIcon"
      isLoading={isLoading}
      isError={isError}
      errorState={{
        title: 'Could not load Clio credentials',
        description: error instanceof Error ? error.message : 'Unknown error',
        onRetry: () => {
          void refetch();
        },
      }}
    >
      <div className="space-y-6">
        <CredentialList
          credentials={sortedCredentials}
          onAdd={() => setAddOpen(true)}
          onReauthorize={handleReauthorize}
          onEditLabel={setEditTarget}
          onToggleEnabled={handleToggleEnabled}
          onRemove={setRemoveTarget}
          busyCredentialId={busyCredentialId}
        />
      </div>

      <AddCredentialDialog
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onAuthorized={invalidate}
        isFirstCredential={isFirstCredential}
      />

      <EditLabelDialog
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        credential={editTarget}
      />

      <ConfirmRemoveDialog
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        credential={removeTarget}
      />
    </PageLayout>
  );
}
